import { Card, Segmented, Skeleton, Typography } from "antd";
import { Banknote } from "lucide-react";
import { useState } from "react";
import type { Currency } from "stores/localeStore";
import { formatCurrencyAmount } from "utility/formatCurrency";
import { useDailyRevenueQuery } from "../hooks/useDailyRevenueQuery";
import type { RevenueMarketFilter } from "../types";

const TONE_UP = "#389e0d";
const TONE_DOWN = "#cf1322";
const TONE_FLAT = "rgba(0,0,0,0.45)";

const MARKET_OPTIONS: { label: string; value: string }[] = [
  { label: "Alla", value: "all" },
  { label: "SEK", value: "SEK" },
  { label: "NOK", value: "NOK" },
  { label: "DKK", value: "DKK" },
];

const fmtSv = (value: number) =>
  new Intl.NumberFormat("sv-SE", {
    maximumFractionDigits: 0,
  }).format(value);

function formatRevenue(value: number, market: RevenueMarketFilter): string {
  if (market) {
    return formatCurrencyAmount(value, market as Currency);
  }
  return `${fmtSv(value)} kr`;
}

function formatDelta(
  current: number,
  previous: number,
  label: string,
): { text: string; tone: string } {
  const diff = current - previous;
  if (diff === 0) {
    return { text: `Oförändrat jämfört med ${label}`, tone: TONE_FLAT };
  }
  const sign = diff > 0 ? "+" : "";
  const tone = diff > 0 ? TONE_UP : TONE_DOWN;
  return {
    text: `${sign}${fmtSv(diff)} jämfört med ${label}`,
    tone,
  };
}

export function DailyRevenueStatCard() {
  const [market, setMarket] = useState<RevenueMarketFilter>(null);
  const { data, isLoading } = useDailyRevenueQuery(market);
  const revenue = data?.revenue ?? 0;
  const vsYesterday = formatDelta(revenue, data?.previousDayRevenue ?? 0, "igår");
  const vsLastYear = formatDelta(revenue, data?.sameDayLastYearRevenue ?? 0, "förra året");

  return (
    <Card
      bordered={false}
      style={{ width: "100%", height: "100%" }}
      bodyStyle={{
        padding: "20px 24px",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 8,
          marginBottom: 8,
        }}
      >
        <Typography.Text type="secondary" style={{ fontSize: 14 }}>
          Dagens omsättning
        </Typography.Text>
        <Segmented
          size="small"
          options={MARKET_OPTIONS}
          value={market ?? "all"}
          onChange={(value) =>
            setMarket(value === "all" ? null : (value as RevenueMarketFilter))
          }
        />
      </div>

      {isLoading ? (
        <Skeleton active paragraph={false} title={{ width: "60%" }} />
      ) : (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 4,
          }}
        >
          <Banknote size={24} strokeWidth={1.75} color="rgba(0,0,0,0.45)" />
          <Typography.Text
            style={{ fontSize: 24, fontWeight: 600, whiteSpace: "nowrap" }}
          >
            {formatRevenue(revenue, market)}
          </Typography.Text>
        </div>
      )}

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          marginTop: "auto",
        }}
      >
        <Typography.Text style={{ fontSize: 13, color: isLoading ? TONE_FLAT : vsYesterday.tone }}>
          {isLoading ? "\u00a0" : vsYesterday.text}
        </Typography.Text>
        <Typography.Text style={{ fontSize: 13, color: isLoading ? TONE_FLAT : vsLastYear.tone }}>
          {isLoading ? "\u00a0" : vsLastYear.text}
        </Typography.Text>
      </div>
    </Card>
  );
}
